import type { IFormProps } from 'components/form/types'
import type { TFormModel } from 'components/form/types/formModel'
import {
  computed,
  isRef,
  reactive,
  shallowRef,
  watch,
  type MaybeRef,
  type Ref,
  type ShallowRef
} from 'vue'
import {
  applyForm,
  castFieldValue,
  evaluateFormState,
  evaluateReactiveProps,
  purifyForm
} from './utils'
import { createFieldsDict } from '../../utils'
import { useFormDeps, type UseFormDepsReturn } from './useFormDeps'
import { reactiveComputed } from '@vueuse/core'
import { isFunction, isNotNullish, isNumerical, isString } from '@/utils/is'
import type { FormLayoutProps, IForm, ReactiveFormLayoutProps } from 'components/form/types/form'
import type { TFormFactor } from 'components/form/types/attributes'
import { useFormStats, type UseFormStatsReturn } from './useFormStats'
import { useFormErrors, type UseFormErrorsOptions, type UseFormErrorsReturn } from './useFormErrors'
import { hashAny, hashAnyu32 } from '@/utils/hash'
import { unique } from '@/utils/array'
import type { FormFieldRef, TFormFieldDict } from 'components/form/types/field'
import type { StringIndexable } from '@/types/object'
import { hasSomeKeys, select, unwrapNestedRefs } from '@/utils/object'
import type { SetState } from '@/types/state/setState'
import type { Dict } from '@/types/dict'
import type { FormSubmit } from '../../types'
import { useKeyboard } from '@/utils/hooks/useKeyboard'
import { onHotReload } from '@/utils/debug/onHotreload'
import { GlobalFormContext } from './global'
import { isVoidish } from '@/types/voidish'

export type UseFormOptions<T extends Dict = Dict> = {
  errors?: UseFormErrorsOptions
  submit?: FormSubmit<T>
}

export type UseFormProps<
  T extends TFormModel = { [key: string]: any },
  ContextType extends StringIndexable | undefined = StringIndexable
> = Omit<IFormProps<T>, 'schema'> & {
  schema: MaybeRef<IForm<T, ContextType> | Ref<IForm<T, ContextType>>>
  options?: UseFormOptions<T>
  onFieldChange?: (field: FormFieldRef, nextValue: TFormFactor) => void
}

export type FormHandlerState = {
  version: string
}

export type UseFormChangeCallbackFunc<T extends TFormModel = StringIndexable> = (state: T) => any
export type UseFormChangeListener<T extends TFormModel = StringIndexable> = {
  fun: UseFormChangeCallbackFunc<T>
  destroy: () => void
}

export type FormHandler<
  T extends TFormModel = StringIndexable,
  ContextType extends StringIndexable | undefined = StringIndexable
> = {
  init: (handler: FormHandler) => void
  stats: UseFormStatsReturn<T, ContextType>
  errors: UseFormErrorsReturn<T, ContextType>
  deps: UseFormDepsReturn<T, ContextType>
  state: Ref<T>
  initialState: T
  schema: IForm<T, ContextType>
  fields: Ref<TFormFieldDict<T>>
  layout: ShallowRef<FormLayoutProps<T>[]>
  reportChange: (field: FormFieldRef, nextValue?: TFormFactor, nullish?: boolean) => void
  handleInput: (event: Event, field: FormFieldRef, fieldSchema: IForm<any>) => void
  handleBlur: (event: Event, field: FormFieldRef, fieldSchema: IForm<any>) => void
  handleFocus: (event: FocusEvent, field: FormFieldRef, fieldSchema: IForm<any>) => void
  triggerBlur: (field: FormFieldRef) => void
  evaluatedState: Ref<T>
  props: UseFormProps<T, ContextType>
  handlerState: FormHandlerState
  setState: SetState<T>
  updateField: (path: string, value: any) => void
  setSchema: SetState<IForm<T, ContextType>>
  selectField: (path: string) => FormFieldRef | undefined
  addSlice: (slice: FormHandler<T, ContextType>) => () => void
  onNextChange: (fun: UseFormChangeCallbackFunc<T>) => () => void
  isFormHandler: 'yes'
}

const createHandler = <
  T extends TFormModel = { [key: string]: any },
  ContextType extends StringIndexable | undefined = StringIndexable
>(
  props: UseFormProps<T, ContextType>
): FormHandler<T, ContextType> => {
  const _formID = GlobalFormContext.newID()
  const slices: ShallowRef<FormHandler<T, ContextType>[]> = shallowRef<
    FormHandler<T, ContextType>[]
  >([])
  const listeners = new Set<UseFormChangeListener<T>>()

  const onNextChange = (fun: UseFormChangeCallbackFunc<T>): (() => void) => {
    const listener: UseFormChangeListener<T> = {
      fun,
      destroy: () => {
        queueMicrotask(() => {
          listeners.delete(listener)
        })
      }
    }

    listeners.add(listener);

    return () => {
      listener.destroy()
    }
  }

  const handlerState = reactive<FormHandlerState>({
    version: '0'
  })

  const schema = reactiveComputed(() =>
    isRef(props.schema) ? props.schema.value : props.schema
  ) as IForm<T, ContextType>

  const initialState = reactiveComputed(() => {
    return (
      props.initialState ||
      purifyForm(props.initialState ? applyForm(schema, props.initialState) : schema)
    )
  }) as T

  const computeNextLayout = (
    state: T,
    layout: ReactiveFormLayoutProps<T> | null | undefined
  ): FormLayoutProps<T> => {
    if (!layout) return {}
    return {
      ...layout,
      $component: layout.$component ? () => (layout.$component as any)({ state }) : undefined,
      $children: (isFunction(layout.$children) ? layout.$children(state) : layout.$children || [])
        .filter((it) => !isVoidish(it) && isNotNullish(it))
        .map((it) => computeNextLayout(state, it as any))
    }
  }

  const layout = shallowRef<FormLayoutProps<T>[]>(
    (props.layout || []).map((it) => computeNextLayout(initialState as T, it))
  )

  const updateLayout = (state: T) => {
    layout.value = (props.layout || []).map((it) => computeNextLayout(state, it))
  }

  const fields = computed(() => {
    return createFieldsDict<T, ContextType>(initialState, schema, -1, {
      state: initialState,
      schema: schema
    })
  })

  const evaluatedState = computed(() => {
    return evaluateFormState(schema, fields.value)
  })

  const stats = useFormStats<T, ContextType>({ ...props, fields })
  const errors = useFormErrors<T, ContextType>({
    ...props,
    fields,
    stats,
    options: props.options?.errors
  })
  const deps = useFormDeps<T, ContextType>({
    ...props,
    fields,
    errors,
    context: schema.$context,
    onDependencyUpdate: (field: FormFieldRef) => {
      const name = field.value.name
      if (!name) return

      queueMicrotask(() => {
        errors.refreshErrors(undefined, true)
      })
    }
  })

  const selectField = (path: string) => {
    return select<any, FormFieldRef>(fields.value, path, false)
  }

  const updateField = (path: string, value: any) => {
    const field = selectField(path)
    if (!field) return
    reportChange(field, value)
  }

  onHotReload(() => {
    deps.updateDeps(schema)
  })

  const formState = computed(() => {
    return evaluateFormState<T, ContextType>(schema, fields.value)
  })

  const init = (_handler: FormHandler) => {
    deps.updateDeps(schema)
    //patchAllFields(fields.value, (_field) => {
    //  // field.value.handler = handler;
    //});
    if (props.hideErrorsUntilTouched) {
      errors.clearErrors()
    } else {
      errors.refreshErrors(undefined, !!props.validateImmediately)
    }
  }

  const broadcast = (state: T) => {
    Array.from(listeners.values()).map((it) => it.fun(state))
  }

  const emitChange = (field: FormFieldRef, evaluatedState: any) => {
    const handle = async () => {
      if (props.onStateChange) {
        await props.onStateChange(evaluatedState)
      }
      if (props.outputState) {
        props.outputState.value = evaluatedState
      }

      if (props.outputStates) {
        props.outputStates.forEach((s) => {
          s.value = evaluatedState
        })
      }

      if (field.value.schema.$validators && field.value.schema.$validators.length > 0) {
        stats.state.touched = true
      }

      stats.state.changeCount += 1

      evaluateReactiveProps(
        field.value.schema,
        field,
        schema as IForm<any>,
        fields.value,
        undefined,
        field
      )
      errors.updateFieldErrors(field)
      deps.updateDependants(field.value.schema, field)
      broadcast(evaluatedState)

      queueMicrotask(() => {
        updateLayout(evaluatedState)
      })
    }

    handle().catch((e) => console.error(e))
  }

  const handleNextChange = (
    field: FormFieldRef,
    sch?: IForm<any>,
    nextValue?: TFormFactor,
    nullish?: boolean
  ) => {
    if (slices.value.length > 0) return
    const fieldSchema = (sch || field.value.schema || {}) as IForm<any>

    if (fieldSchema.$type === 'number') {
      if (
        !fieldSchema.$numberPrecision &&
        isString(nextValue) &&
        !isNumerical(nextValue) &&
        nextValue !== '0'
      ) {
        nextValue = 0
      }
    }

    if (field.value.value !== nextValue && field.value.path) {
      const u32_a = hashAnyu32(handlerState.version)
      const u32_b = hashAnyu32(nextValue + '')
      handlerState.version = hashAny(u32_a + u32_b + '')
      field.value.touched = true
      stats.setTouched(true)
    }

    if (fieldSchema.$type === 'value' && typeof nextValue !== 'undefined' && nextValue !== null) {
      nextValue = castFieldValue(fieldSchema, nextValue)
      const oldValue = field.value.value

      if (typeof nextValue === 'object' && typeof oldValue === 'object') {
        nextValue = {
          ...oldValue,
          ...nextValue
        }
      }
    }

    field.value.value = nullish ? undefined : (nextValue ?? '')

    deps.clearDependingFields(field, fieldSchema)
    deps.updateDependants(fieldSchema, field)
    deps.clearDependingFields(field, fieldSchema)

    const evaluated = evaluateFormState(schema, fields.value)

    errors.refreshErrors(evaluated)

    emitChange(field, evaluated)

    if (props.onFieldChange) {
      props.onFieldChange(field, nextValue)
    }
  }

  // Custom onInput callback (For custom components, such as DatePicker etc...)
  const reportChange = (field: FormFieldRef, nextValue?: TFormFactor, nullish?: boolean) => {
    if (!field) throw new Error('reportChange(): Missing `field`')
    handleNextChange(
      field,
      field.value.schema,
      nullish ? undefined : (nextValue ?? field.value.value),
      nullish
    )
  }

  // Native "normal" onInput callback
  const handleInput = (event: Event, field: FormFieldRef, fieldSchema: IForm<any>) => {
    const e = event as Omit<Event, 'target'> & { target: HTMLInputElement }
    handleNextChange(field, fieldSchema, e.target.value)
  }

  const triggerBlur = (field: FormFieldRef) => {
    if (props.options?.errors?.showErrorsOnBlur) {
      errors.setFieldErrorsVisible(field, true)
    }
  }

  const handleBlur = (_event: Event, field: FormFieldRef, _fieldSchema: IForm<any>) => {
    triggerBlur(field)
  }

  const handleFocus = (_event: FocusEvent, field: FormFieldRef, _fieldSchema: IForm<any>) => {
    if (props.options?.errors?.showErrorsOnBlur) {
      errors.setFieldErrorsVisible(field, false)
    }
  }

  const setState: SetState<T> = (fun) => {
    const next = fun(initialState)

    const patchFields = (data: any, nextSchema: IForm, path: string[] = []) => {
      for (const [key, value] of Object.entries(data)) {

        const sch = nextSchema[key]
        if (!sch) {
          const fieldPath = [...path, key].join('.');
          const field = selectField(fieldPath);
          if (field) {
            updateField(fieldPath, value);
          } else {
            console.error(`Invalid ${key}, path=${path.join('.')}`)
          }
          continue
        }
        const nextPath = [...path, key].join('.')
        if (hasSomeKeys(sch, ['$value', '$type'])) {
          updateField(nextPath, value)
          continue
        }
        if (typeof value !== 'object') continue
        patchFields(value, sch as any, [...path, key])
      }
    }

    patchFields(unwrapNestedRefs(next), schema as any)
  }

  const setSchema: SetState<IForm<T, ContextType>> = (fun) => {
    const next = fun(schema)

    for (const [key, value] of Object.entries(next)) {
      (schema as any)[key] = value || (schema as any)[key];
    }
  }

  const submitFn = props.options?.submit?.action
  if (submitFn) {
    const kb = useKeyboard()
    kb.addEventListener('keypress', (btn) => {
      if (btn.key === 'Enter') {
        submitFn(formState.value)
      }
    })
  }

  if (props.syncedState) {
    watch(
      props.syncedState,
      (next) => {
        console.log({ synced: next })
        if (!next) return;

        setState((s) => {
          return { ...s, ...next }
        })

        slices.value.forEach(it => {
          it.setState(k => ({...k, ...next}))
        })

      },
      { immediate: true }
    )
  }

  const addSlice = (slice: FormHandler<T, ContextType>) => {
    slices.value = [...slices.value, slice]
    return () => {
      slices.value = slices.value.filter((it) => it !== slice)
    }
  }

  return {
    init,
    stats,
    errors,
    fields,
    deps,
    state: formState,
    initialState,
    schema,
    reportChange,
    handleInput,
    handleBlur,
    handleFocus,
    triggerBlur,
    evaluatedState,
    handlerState,
    setState,
    setSchema,
    props,
    selectField,
    updateField,
    onNextChange,
    layout: layout,
    addSlice,
    isFormHandler: 'yes'
  }
}

type HandlerMap = Record<string, FormHandler<any, any>>

type UseFormGlobalState = {
  handlerMap: HandlerMap
  stack: string[]
}

const globalState: UseFormGlobalState = {
  handlerMap: {},
  stack: []
}

export const getCurrentForm = <T extends TFormModel = TFormModel>(): FormHandler<T> | undefined => {
  if (globalState.stack.length <= 0) return undefined
  const key = globalState.stack[globalState.stack.length - 1]
  const handler = globalState.handlerMap[key]
  if (!handler) return undefined
  return handler as unknown as FormHandler<T>
}

export const getFormById = <T extends TFormModel = TFormModel>(
  id: string
): FormHandler<T> | undefined => {
  const handler = globalState.handlerMap[id]
  if (!handler) return undefined
  return handler
}

export const useForm = <
  T extends TFormModel = { [key: string]: any },
  ContextType extends StringIndexable | undefined = StringIndexable
>(
  props: UseFormProps<T, ContextType>
) => {
  const initial = createHandler<T, ContextType>(props)
  const handler = new Proxy<typeof initial>(initial, {
    set(target: typeof initial, p: string | symbol, newValue: any) {
      const key = p as keyof typeof target
      target[key] = newValue
      return true
    },
    get(target: typeof initial, p: string | symbol) {
      const key = p as keyof typeof target
      return target[key]
    }
  })

  const uid = handler.schema.$formId || hashAny(new Error())

  const addHandler = () => {
    globalState.stack = unique([...globalState.stack, uid])
    globalState.handlerMap[uid] = handler as FormHandler<T, ContextType>
  }

  addHandler()

  return handler
}

export type UseFormReturn<
  T extends TFormModel = { [key: string]: any },
  ContextType extends StringIndexable | undefined = StringIndexable
> = FormHandler<T, ContextType>
